import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import type { Document } from "@/types/document";
import { useState, useEffect } from "react";
import { DocumentPreviewDialog } from "@/components/DocumentPreviewDialog";
import { DocumentList } from "@/components/DocumentList";
import { DocumentDownloadDialog } from "@/components/DocumentDownloadDialog";
import { useDocumentOperations } from "@/hooks/use-document-operations";
import { useNavigate } from "react-router-dom";

export default function Documents() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [userId, setUserId] = useState<string | null>(null);
  const {
    previewDocument,
    setPreviewDocument,
    previewContent,
    setPreviewContent,
    downloadDocument,
    setDownloadDocument,
    handlePreview,
    deleteMutation,
  } = useDocumentOperations();

  useEffect(() => {
    const getUserId = async () => {
      console.log('Getting user session...');
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError) {
        console.error('Session error:', sessionError);
      }
      
      if (session) {
        console.log('User authenticated:', session.user.id);
        console.log('User email:', session.user.email);
        setUserId(session.user.id);
      } else {
        console.error('No authenticated user found');
        toast({
          title: "Authentication Error",
          description: "Please log in to view your documents",
          variant: "destructive",
        });
        navigate('/auth');
      }
    };
    getUserId();
  }, [toast, navigate]);

  const { data: documents, isLoading, error: queryError, refetch } = useQuery({
    queryKey: ['documents', userId],
    queryFn: async () => {
      if (!userId) {
        console.log('No userId available, skipping fetch');
        return [];
      }

      console.log('Fetching documents for user:', userId);
      
      // First, let's check if the user exists and has any documents
      const { data: userCheck, error: userError } = await supabase
        .from('documents')
        .select('count')
        .eq('user_id', userId);
      
      console.log('User check result:', userCheck, userError);
      
      const { data, error } = await supabase
        .from('documents')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching documents:', error);
        throw error;
      }

      console.log('Documents fetched:', data);
      console.log('Number of documents:', data?.length || 0);
      return data as Document[];
    },
    enabled: !!userId,
  });

  const handleRefresh = async () => {
    console.log('Manually refreshing documents');
    await refetch();
    toast({
      title: "Refreshed",
      description: "Document list has been updated",
    });
  };

  if (!userId) {
    return <div className="p-6">Please log in to view your documents.</div>;
  }

  if (isLoading) {
    return <div className="p-6">Loading...</div>;
  }

  if (queryError) {
    console.error('Query error:', queryError);
    return (
      <div className="p-6">
        <div className="text-red-500 mb-4">Error loading documents: {queryError.message}</div>
      </div>
    );
  }

  if (!documents || documents.length === 0) {
    return (
      <div className="p-6">
        <div className="mb-6">
          <h1 className="text-3xl font-bold">My Documents</h1>
        </div>
        <div className="text-gray-500 mb-4">
          No documents found. Create your first document from a template.
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold">My Documents</h1>
      </div>
      
      <DocumentList
        documents={documents}
        onPreview={handlePreview}
        onDownload={setDownloadDocument}
        onDelete={(id) => deleteMutation.mutate(id)}
        onRefresh={handleRefresh}
      />

      {previewDocument && (
        <DocumentPreviewDialog
          isOpen={!!previewDocument}
          onClose={() => {
            setPreviewDocument(null);
            setPreviewContent("");
          }}
          html={previewContent}
          filename={previewDocument.filename}
        />
      )}

      <DocumentDownloadDialog
        isOpen={!!downloadDocument}
        onClose={() => setDownloadDocument(null)}
        document={downloadDocument}
      />
    </div>
  );
}
